import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import axios from 'axios'
import ImageUploader from './ImageUploader'

type MaskEngine = 'bodypix' | 'sam3'

type BodyPixTarget =
  | 'person'
  | 'torso'
  | 'head'
  | 'arms'
  | 'hands'
  | 'legs'
  | 'feet'

const BODYPIX_PART_IDS = {
  left_face: 0,
  right_face: 1,
  left_upper_arm_front: 2,
  left_upper_arm_back: 3,
  right_upper_arm_front: 4,
  right_upper_arm_back: 5,
  left_lower_arm_front: 6,
  left_lower_arm_back: 7,
  right_lower_arm_front: 8,
  right_lower_arm_back: 9,
  left_hand: 10,
  right_hand: 11,
  torso_front: 12,
  torso_back: 13,
  left_upper_leg_front: 14,
  left_upper_leg_back: 15,
  right_upper_leg_front: 16,
  right_upper_leg_back: 17,
  left_lower_leg_front: 18,
  left_lower_leg_back: 19,
  right_lower_leg_front: 20,
  right_lower_leg_back: 21,
  left_foot: 22,
  right_foot: 23
} as const

const BODYPIX_GROUPS: Record<BodyPixTarget, number[] | null> = {
  person: null,
  torso: [BODYPIX_PART_IDS.torso_front, BODYPIX_PART_IDS.torso_back],
  head: [BODYPIX_PART_IDS.left_face, BODYPIX_PART_IDS.right_face],
  arms: [
    BODYPIX_PART_IDS.left_upper_arm_front,
    BODYPIX_PART_IDS.left_upper_arm_back,
    BODYPIX_PART_IDS.right_upper_arm_front,
    BODYPIX_PART_IDS.right_upper_arm_back,
    BODYPIX_PART_IDS.left_lower_arm_front,
    BODYPIX_PART_IDS.left_lower_arm_back,
    BODYPIX_PART_IDS.right_lower_arm_front,
    BODYPIX_PART_IDS.right_lower_arm_back
  ],
  hands: [BODYPIX_PART_IDS.left_hand, BODYPIX_PART_IDS.right_hand],
  legs: [
    BODYPIX_PART_IDS.left_upper_leg_front,
    BODYPIX_PART_IDS.left_upper_leg_back,
    BODYPIX_PART_IDS.right_upper_leg_front,
    BODYPIX_PART_IDS.right_upper_leg_back,
    BODYPIX_PART_IDS.left_lower_leg_front,
    BODYPIX_PART_IDS.left_lower_leg_back,
    BODYPIX_PART_IDS.right_lower_leg_front,
    BODYPIX_PART_IDS.right_lower_leg_back
  ],
  feet: [BODYPIX_PART_IDS.left_foot, BODYPIX_PART_IDS.right_foot]
}

function normalizePrompt(prompt: string) {
  return (prompt || '').toLowerCase().trim()
}

function inferBodyPixTargetFromPrompt(prompt: string): BodyPixTarget {
  const p = normalizePrompt(prompt)
  if (!p) return 'torso'
  if (p.includes('full') || p.includes('person') || p.includes('whole') || p.includes('cała') || p.includes('cala')) {
    return 'person'
  }
  if (p.includes('torso') || p.includes('korpus') || p.includes('tul') || p.includes('body')) return 'torso'
  if (p.includes('head') || p.includes('face') || p.includes('twarz') || p.includes('głowa') || p.includes('glowa')) {
    return 'head'
  }
  if (p.includes('hands') || p.includes('hand') || p.includes('dłonie') || p.includes('dlonie')) return 'hands'
  if (p.includes('arms') || p.includes('arm') || p.includes('ręce') || p.includes('rece')) return 'arms'
  if (p.includes('feet') || p.includes('foot') || p.includes('stopy') || p.includes('stopa')) return 'feet'
  if (p.includes('legs') || p.includes('leg') || p.includes('nogi')) return 'legs'
  return 'torso'
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Nie udało się wczytać obrazu'))
    img.src = dataUrl
  })
}

async function generateBodyPixMask(dataUrl: string, prompt: string, invert: boolean): Promise<string> {
  const img = await loadImage(dataUrl)

  const tf = await import('@tensorflow/tfjs')
  const bodyPix = await import('@tensorflow-models/body-pix')

  try {
    if (tf.getBackend() === 'cpu') {
      await tf.setBackend('webgl')
    }
  } catch {
    // ignore backend switch errors
  }

  const net = await getBodyPixNet(bodyPix)

  const segmentation = await net.segmentPersonParts(img, {
    flipHorizontal: false,
    internalResolution: 'medium',
    segmentationThreshold: 0.7
  })

  const target = inferBodyPixTargetFromPrompt(prompt)
  const selectedPartIds = BODYPIX_GROUPS[target]

  const canvas = document.createElement('canvas')
  canvas.width = segmentation.width
  canvas.height = segmentation.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Nie udało się utworzyć canvas')

  const maskImage = ctx.createImageData(segmentation.width, segmentation.height)
  const out = maskImage.data
  const partData = segmentation.data as unknown as Int32Array

  for (let i = 0; i < partData.length; i++) {
    const partId = partData[i]
    const isPerson = partId !== -1
    const isSelected = selectedPartIds === null ? isPerson : selectedPartIds.includes(partId)
    const v = invert ? (isSelected ? 0 : 255) : (isSelected ? 255 : 0)
    const base = i * 4
    out[base] = v
    out[base + 1] = v
    out[base + 2] = v
    out[base + 3] = 255
  }

  ctx.putImageData(maskImage, 0, 0)
  return canvas.toDataURL('image/png')
}

let bodyPixNetPromise: Promise<any> | null = null
async function getBodyPixNet(bodyPix: any) {
  if (!bodyPixNetPromise) {
    bodyPixNetPromise = bodyPix.load({
      architecture: 'MobileNetV1',
      outputStride: 16,
      multiplier: 0.75,
      quantBytes: 2
    })
  }
  return bodyPixNetPromise
}

const MaskTool: React.FC = () => {
  const [inputImage, setInputImage] = useState<string>('')
  const [prompt, setPrompt] = useState<string>('torso')
  const [engine, setEngine] = useState<MaskEngine>('bodypix')
  const [invert, setInvert] = useState<boolean>(false)
  const [maskImage, setMaskImage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const inferredTarget = useMemo(() => inferBodyPixTargetFromPrompt(prompt), [prompt])

  const handleImageUpload = (imageData: string) => {
    setInputImage(imageData)
    setMaskImage('')
    setError('')
  }

  const handleGenerate = async () => {
    if (!inputImage) {
      setError('Wgraj obraz wejściowy')
      return
    }
    if (!prompt.trim()) {
      setError('Wpisz prompt (np. "torso", "hands", "head")')
      return
    }

    setLoading(true)
    setError('')
    setMaskImage('')

    try {
      if (engine === 'bodypix') {
        const dataUrl = await generateBodyPixMask(inputImage, prompt, invert)
        setMaskImage(dataUrl)
        return
      }

      const token = localStorage.getItem('token')
      const response = await axios.post(
        '/api/photo-tools/mask',
        { image: inputImage, engine: 'sam3', prompt, invert },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data?.status === 'success') {
        setMaskImage(`data:image/png;base64,${response.data.mask_image}`)
      } else {
        setError(response.data?.error || 'Nie udało się wygenerować maski')
      }
    } catch (err: any) {
      console.error('Mask error:', err)
      setError(err.response?.data?.error || 'Wystąpił błąd podczas generowania maski')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!maskImage) return
    const link = document.createElement('a')
    link.download = 'mask.png'
    link.href = maskImage
    link.click()
  }

  const handleReset = () => {
    setInputImage('')
    setMaskImage('')
    setError('')
    setPrompt('torso')
    setEngine('bodypix')
    setInvert(false)
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Obraz wejściowy
            </Typography>
            <ImageUploader label="Wejście" onImageUpload={handleImageUpload} currentImage={inputImage} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ustawienia
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Silnik</InputLabel>
              <Select
                value={engine}
                label="Silnik"
                onChange={(e) => setEngine(e.target.value as MaskEngine)}
              >
                <MenuItem value="bodypix">BodyPix (lokalnie)</MenuItem>
                <MenuItem value="sam3">SAM3 (serwer)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder='np. "torso", "hands", "head", "full body"'
              size="small"
              sx={{ mb: 1 }}
            />
            {engine === 'bodypix' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                BodyPix: prompt mapuje na części ciała (wykryto: {inferredTarget})
              </Typography>
            )}

            <FormControlLabel
              control={<Switch checked={invert} onChange={(e) => setInvert(e.target.checked)} />}
              label="Odwróć maskę (invert)"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                fullWidth
              >
                Generuj maskę
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
                startIcon={<RestartAltIcon />}
              >
                Reset
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {maskImage && (
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Wynik
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Oryginał
              </Typography>
              <Box
                component="img"
                src={inputImage}
                alt="Input"
                sx={{
                  width: '100%',
                  maxHeight: 360,
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: 'grey.50'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Maska (PNG)
              </Typography>
              <Box
                component="img"
                src={maskImage}
                alt="Mask"
                sx={{
                  width: '100%',
                  maxHeight: 360,
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: 'grey.50'
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Pobierz maskę
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default MaskTool
